/*
 * Copyright 2025 iodyne.  All rights reserved.
 * Use is subject to license terms.
 */
import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { type TypedUseSelectorHook, useDispatch as useDispatchGeneric, useSelector as useSelectorGeneric } from "react-redux";
import { AuthStates, setAuthState } from "@/lib/auth/auth_state";
//@ts-ignore
import { logger } from 'redux-logger';
import type { Middleware } from '@reduxjs/toolkit';
import { orgSlice } from "@/lib/datastore/slices/org";
import { devicesSlice } from '@/lib/datastore/slices/devices';
import { usersSlice } from '@/lib/datastore/slices/users';
import { teamsSlice } from '@/lib/datastore/slices/teams';
import { firmwareSlice } from '@/lib/datastore/slices/firmware';
import { projectsSlice } from '@/lib/datastore/slices/projects';

const invalidTokenErrors:string[] = [
  "InvalidAccessTokenError",
  "ExpiredAccessTokenError"
]

// Auth Error Middleware
const authMiddleware:Middleware = (_store) => (next) => (action) => {
  const resp_status: number | undefined = (action as any).meta?.baseQueryMeta?.response?.status;
  if (resp_status === 401) {
    if (
      (action as any).payload?.data && 
      !invalidTokenErrors.includes((action as any).payload.data.error_class)
    ) {
      return next(action);
    }
    setAuthState(AuthStates.forceLogOut, true);
    window.location.href = "/";
  } else { 
   return next(action);
  }
}

//Combine Reducers
const rootReducer = combineReducers({
  [devicesSlice.reducerPath]: devicesSlice.reducer,
  [orgSlice.reducerPath]: orgSlice.reducer,
  [usersSlice.reducerPath]: usersSlice.reducer,
  [firmwareSlice.reducerPath]: firmwareSlice.reducer,
  [projectsSlice.reducerPath]: projectsSlice.reducer,
  [teamsSlice.reducerPath]: teamsSlice.reducer
});

//Configure store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    const base_mw = getDefaultMiddleware(
    ).concat(authMiddleware
    ).concat(orgSlice.middleware
    ).concat(devicesSlice.middleware
    ).concat(usersSlice.middleware
    ).concat(firmwareSlice.middleware
    ).concat(projectsSlice.middleware
    ).concat(teamsSlice.middleware
    );
    
    if(process.env.ENABLE_REDUX_LOGGING == "true") {
      return base_mw.concat(logger);
    }
    return base_mw;
  }
});

//Typed useDispatch and useSelector convenience methods
export type RootState = ReturnType<typeof store.getState>;
export type AppStore = typeof store;
export type AppDispatch = AppStore['dispatch'];
export const useDispatch = () => useDispatchGeneric<AppDispatch>();
export const useSelector:TypedUseSelectorHook<RootState> = useSelectorGeneric;
export const slices = {
  [devicesSlice.reducerPath]: devicesSlice,
  [orgSlice.reducerPath]: orgSlice,
  [usersSlice.reducerPath]: usersSlice,
  [firmwareSlice.reducerPath]: firmwareSlice,
  [projectsSlice.reducerPath]: projectsSlice,
  [teamsSlice.reducerPath]: teamsSlice
}
